// DbNameCell — the Form meta & SQL table's editable table-name cell, and the
// repo's first inline-editable DataTable cell.
//
// The value it holds is a DRAFT, not a saved field: there is no endpoint that
// persists a table name on its own, so the name is committed only when the row's
// data connection is switched on (see DataConnectionCell). That is why the draft
// lives on the page rather than in here — the Switch, a different cell, has to
// read it.
//
// Interaction follows WorkflowStepCard's title editor, the nearest thing this
// codebase has to an inline editor: pencil swaps the text for an Input, Enter or
// blur commits, Escape reverts. No check/✗ icon pair — nothing here uses one.
//
// A cell has nowhere to hang a Field error, so an INVALID value keeps the cell
// in edit mode with the input marked, and the reason rides in `title` rather
// than being silently dropped or silently accepted.

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton, Input } from '@/components/common';
import sqlIdentifierError, { SQL_IDENTIFIER_MAX } from '@/utils/sqlIdentifier';
import type { DataSetupForm } from '@/api/endpoints/dataSetupApi';

interface DbNameCellProps {
  form: DataSetupForm;
  /** Draft if the user has typed one, else the persisted name, else ''. */
  value: string;
  /** Commit a valid draft up to the page. Never fires for an invalid value. */
  onCommit: (formId: string, next: string) => void;
}

const DbNameCell = ({ form, value, onCommit }: DbNameCellProps) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Blur fires on Escape's unmount path too; this keeps it from re-committing
  // the value Escape just discarded.
  const cancelled = useRef(false);

  // Re-sync when the row's saved value changes underneath us (a refetch after
  // connecting), but never while the user is mid-edit.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Connected rows are locked: the name is live in the warehouse, so it can only
  // change by disconnecting first. This mirrors the design, where every
  // connected row shows a lock and every disconnected one a pencil.
  const locked = form.dbConnection;
  const errorRule = sqlIdentifierError(draft);
  const errorText = errorRule
    ? t(`data_setup_meta_db_name_${errorRule}`, { max: SQL_IDENTIFIER_MAX })
    : undefined;

  const startEdit = () => {
    cancelled.current = false;
    setDraft(value);
    setEditing(true);
  };

  const commit = () => {
    if (cancelled.current) return;
    // Invalid: stay in edit mode rather than discarding what was typed.
    if (errorRule) return;
    const next = draft.trim();
    setEditing(false);
    if (next !== value) onCommit(form.id, next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      cancelled.current = true;
      setDraft(value);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <span className='data-setup-table__db-name'>
        <Input
          className='data-setup-table__db-name-input'
          size='sm'
          autoFocus
          // SQL identifiers are always latin — keep them LTR under Arabic.
          dir='ltr'
          maxLength={SQL_IDENTIFIER_MAX}
          value={draft}
          error={Boolean(errorRule)}
          title={errorText}
          aria-label={t('data_setup_meta_db_name_edit')}
          aria-invalid={Boolean(errorRule) || undefined}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={onKeyDown}
        />
      </span>
    );
  }

  return (
    <span className='data-setup-table__db-name'>
      {value ? (
        <code className='data-setup-table__sql' dir='ltr'>
          {value}
        </code>
      ) : (
        <span className='data-setup-table__db-name-empty'>{t('data_setup_meta_db_name_ph')}</span>
      )}
      {locked ? (
        <Icon
          name='lock'
          size={14}
          className='data-setup-table__db-name-lock'
          aria-label={t('data_setup_meta_db_name_locked')}
        />
      ) : (
        <IconButton
          icon='pencil'
          variant='ghost'
          size='sm'
          aria-label={t('data_setup_meta_db_name_edit')}
          onClick={startEdit}
        />
      )}
    </span>
  );
};

export default DbNameCell;
